import { alpha, keyframes, styled } from '@mui/material/styles';
import React from 'react';

const INDICATOR_SIZE = 48;
// Sits at the MUI modal layer so the overlay covers app chrome/content
const OVERLAY_Z_INDEX = 1300;

const CUBE_ANIMATION_DURATION = '1.3s';
// Staggered start times (in seconds) for the 3x3 grid of cubes
const CUBE_DELAYS = [0.2, 0.3, 0.4, 0.1, 0.2, 0.3, 0, 0.1, 0.2];

const cubePulse = keyframes`
  0%,
  70%,
  100% {
    transform: scale3d(1, 1, 1);
  }
  35% {
    transform: scale3d(0.05, 0.05, 1);
  }
`;

// Covers the whole viewport for global loads (e.g. switching modules).
const FullPageOverlay = styled('div')`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) =>
    alpha(theme.palette.background.default, 0.875)};
  z-index: ${OVERLAY_Z_INDEX};
`;

// Fills the nearest positioned ancestor (e.g. the main content area) instead of
// the whole viewport, so the header and side navigation stay visible.
const ContainedOverlay = styled('div')`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) =>
    alpha(theme.palette.background.default, 0.875)};
  z-index: ${OVERLAY_Z_INDEX};
`;

const Spinner = styled('div')`
  width: ${INDICATOR_SIZE}px;
  height: ${INDICATOR_SIZE}px;
`;

// Uses the active module theme's primary color so each module's loader is
// tinted with its own brand color.
const Cube = styled('div')`
  width: 33.3333%;
  height: 33.3333%;
  float: left;
  background-color: ${({ theme }) => theme.palette.primary.main};
  animation: ${cubePulse} ${CUBE_ANIMATION_DURATION} infinite ease-in-out;
`;

function LoadingIndicator() {
  return (
    <Spinner
      role="progressbar"
      aria-label="Loading"
      data-testid="page-loading-indicator"
    >
      {CUBE_DELAYS.map((delay, index) => (
        <Cube key={index} style={{ animationDelay: `${delay}s` }} />
      ))}
    </Spinner>
  );
}

interface PageLoadingProps {
  /** is the loading indicator shown */
  open: boolean;
  /**
   * When true, the loader is rendered inline and only covers its nearest
   * positioned ancestor (e.g. the main content area) rather than the whole
   * page. Use for in-app data/route loading so the header and side navigation
   * remain visible. Defaults to a full-page overlay (global app load).
   */
  contained?: boolean;
}

/** Full-page or contained overlay with the shared blocks loading indicator */
export function PageLoading({ open, contained = false }: PageLoadingProps) {
  if (!open) {
    return null;
  }

  const Overlay = contained ? ContainedOverlay : FullPageOverlay;

  return (
    <Overlay>
      <LoadingIndicator />
    </Overlay>
  );
}
